import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import ChatService from './ChatService';
import chatEventService from './ChatEventService';
import {
  getChatMode,
  getConversationId,
  getConversationType,
  patchConversationsWithMessage,
} from './chatConversationUtils';
import { getToken, getUserTypeFromToken, getUserIdFromToken } from '../utils/auth/tokenManager';

const ChatContext = createContext(null);
// Split contexts: `state` changes frequently (conversations, unread, active
// thread) while `actions` are stable for the provider's lifetime. Components
// that only dispatch actions can subscribe via `useChatActions` and avoid
// re-rendering when chat state changes.
const ChatStateContext = createContext(null);
const ChatActionsContext = createContext(null);

export function useChat() {
  return useContext(ChatContext);
}

export function useChatState() {
  return useContext(ChatStateContext);
}

export function useChatActions() {
  return useContext(ChatActionsContext);
}

function decodeTokenUserType() {
  return getUserTypeFromToken(getToken());
}

function isChatParticipant(userType) {
  return userType === 'brand' || userType === 'influencer';
}

function resolveChatUserType(propType, tokenType) {
  if (propType === 'admin' || tokenType === 'admin') return null;
  if (isChatParticipant(propType)) return propType;
  if (isChatParticipant(tokenType)) return tokenType;
  return null;
}

function decodeTokenUserId() {
  return getUserIdFromToken(getToken());
}

function findConversationByTarget(list, conversationId, conversationType) {
  if (!Array.isArray(list) || conversationId == null || conversationId === '') return null;
  const target = String(conversationId);
  return list.find((conversation) => {
    if (conversationType && getConversationType(conversation) !== conversationType) {
      return false;
    }
    return String(getConversationId(conversation)) === target
      || String(conversation.id) === target;
  }) || null;
}

export default function ChatProvider({ children, userType: userTypeProp }) {
  const [isOpen, setIsOpen] = useState(false);
  const [userType, setUserType] = useState(
    () => resolveChatUserType(userTypeProp, decodeTokenUserType()),
  );
  const [userId, setUserId] = useState(() => decodeTokenUserId());
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loadingConversations, setLoadingConversations] = useState(false);
  // While Request update / deep-link open is resolving, FloatingChat must not
  // steal focus from the intended thread. Inbox open never auto-selects a row.
  const [pendingOpenConversationId, setPendingOpenConversationId] = useState(null);
  const pendingOpenConversationIdRef = useRef(null);

  const activeConversationRef = useRef(null);
  useEffect(() => {
    activeConversationRef.current = activeConversation;
  }, [activeConversation]);

  const conversationsRef = useRef(conversations);
  useEffect(() => {
    conversationsRef.current = conversations;
  }, [conversations]);

  const onNewMessageCallbackRef = useRef(null);

  /** Coalesce rapid SSE newMessage events into one conversations refresh */
  const sseConversationFetchTimeoutRef = useRef(null);

  const lastFetchTimeRef = useRef(0);
  const MIN_FETCH_INTERVAL = 15000; // Minimum 15 seconds between fetches

  const conversationFetchCountRef = useRef(0);

  const fetchConversationsRef = useRef(null);

  const fetchConversations = useCallback(async (force = false) => {
    const tokenType = decodeTokenUserType();
    if (!isChatParticipant(userType) || !isChatParticipant(tokenType)) {
      return [];
    }

    const now = Date.now();
    const timeSinceLastFetch = now - lastFetchTimeRef.current;

    // Throttle: skip if fetched recently (unless forced)
    if (!force && timeSinceLastFetch < MIN_FETCH_INTERVAL) {
      return null;
    }

    lastFetchTimeRef.current = now;
    conversationFetchCountRef.current += 1;
    setLoadingConversations(true);
    try {
      const token = getToken();
      if (!token) return null;
      const currentUserType = userType; // Capture current userType
      const list = await ChatService.fetchConversations(currentUserType);

      const filtered = (list || [])
        .map((c) => ({ ...c, chat_mode: getChatMode(c) }))
        .filter((c) => ['active', 'negotiate', 'closed'].includes(c.chat_mode));

      setConversations(filtered);
      return filtered;
    } catch (e) {
      console.error('Error fetching conversations', e);
      return null;
    } finally {
      setLoadingConversations(false);
    }
  }, [userType]); // Removed activeConversation from dependencies to prevent re-creating callback!

  fetchConversationsRef.current = fetchConversations;

  useEffect(() => {
    const newUserType = resolveChatUserType(userTypeProp, decodeTokenUserType());
    const newUserId = decodeTokenUserId();
    /* istanbul ignore else -- the else branch is a legacy defensive fallback. */
    if (newUserType !== userType) {
      setUserType(newUserType);
    } else if (!newUserType && userType) {
      setUserType(null);
    }
    if (String(newUserId ?? '') !== String(userId ?? '')) {
      setUserId(newUserId ?? null);
    }
  }, [userTypeProp, userType, userId]);

  // This is a fallback if userTypeProp is not provided
  useEffect(() => {
    if (!userType && !userTypeProp) {
      const checkUserType = () => {
        const currentUserType = resolveChatUserType(userTypeProp, decodeTokenUserType());
        if (currentUserType !== userType) {
          setUserType(currentUserType);
        }
        const currentUserId = decodeTokenUserId();
        if (String(currentUserId ?? '') !== String(userId ?? '')) {
          setUserId(currentUserId ?? null);
        }
      };

      checkUserType();

      const interval = setInterval(checkUserType, 200);
      const timeout = setTimeout(() => {
        clearInterval(interval);
      }, 5000);

      return () => {
        clearInterval(interval);
        clearTimeout(timeout);
      };
    }
    return undefined;
  }, [userType, userTypeProp, userId]);

  const hasInitialFetchRef = useRef(false);

  useEffect(() => {
    if (!userType) {
      return undefined;
    }

    if (isOpen || !hasInitialFetchRef.current) {
      if (fetchConversationsRef.current) {
        fetchConversationsRef.current(true);
        hasInitialFetchRef.current = true;
      }
    }

    return undefined;
  }, [isOpen, userType]);

  useEffect(() => {
    if (!userType) return undefined;

    const handleVisibilityChange = () => {
      if (!document.hidden && isOpen && fetchConversationsRef.current) {
        fetchConversationsRef.current(false);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isOpen, userType]);

  const open = useCallback(() => {
    if (!pendingOpenConversationIdRef.current) {
      setActiveConversation(null);
    }
    setIsOpen(true);
  }, []);
  const close = useCallback(() => {
    setIsOpen(false);
    setActiveConversation(null);
  }, []);
  const toggle = useCallback(() => {
    setIsOpen((prev) => {
      const next = !prev;
      if (!next || !pendingOpenConversationIdRef.current) {
        setActiveConversation(null);
      }
      return next;
    });
  }, []);

  const openWithConversation = useCallback(async (conversationId, options = {}) => {
    const conversationType = options.conversationType || null;
    const target = conversationId != null && conversationId !== ''
      ? String(conversationId)
      : null;

    if (!target) {
      pendingOpenConversationIdRef.current = null;
      setPendingOpenConversationId(null);
      setActiveConversation(null);
      setIsOpen(true);
      return;
    }

    pendingOpenConversationIdRef.current = target;
    setPendingOpenConversationId(target);

    // Prefer a synchronous select from the cached inbox so the correct thread
    // is active before FloatingChat's auto-select effect can steal focus.
    const cachedMatch = findConversationByTarget(
      conversationsRef.current,
      target,
      conversationType,
    );
    if (cachedMatch) {
      setActiveConversation(cachedMatch);
    } else {
      setActiveConversation(null);
    }
    setIsOpen(true);

    const filtered = await fetchConversations(true);
    if (pendingOpenConversationIdRef.current !== target) return;

    const list = Array.isArray(filtered) ? filtered : (conversationsRef.current || []);
    const found = findConversationByTarget(list, target, conversationType);
    pendingOpenConversationIdRef.current = null;
    setPendingOpenConversationId(null);
    if (found) setActiveConversation(found);
  }, [fetchConversations]);

  useEffect(() => {
    if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
      window.chatProviderFetchCount = conversationFetchCountRef.current;
    }
  }, [conversations]);

  useEffect(() => {
    if (!userType) {
      chatEventService.disconnect();
      return undefined;
    }

    const token = getToken();
    if (!token) {
      return undefined;
    }

    let hasConnected = false;

    const connectToEvents = () => {
      if (hasConnected || document.hidden) return;
      hasConnected = true;
      chatEventService.connect();
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        chatEventService.disconnect();
        hasConnected = false;
        return;
      }

      if (isOpen) connectToEvents();
    };

    if (isOpen) {
      connectToEvents();
    } else {
      window.addEventListener('pointerdown', connectToEvents, { once: true, passive: true });
      window.addEventListener('keydown', connectToEvents, { once: true });
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);

    const unsubscribe = chatEventService.addListener((eventData) => {
      if (eventData.type === 'newMessage') {
        const { conversationType, conversationId, message } = eventData;

        // Prefer patching the matching conversation's last-message metadata from
        // the SSE payload instead of refetching the whole list. Only fall back to
        // a (debounced) refetch when the conversation isn't known yet — e.g. a
        // newly accepted invite whose first message just arrived.
        // NOTE: decide against the ref (synchronous, up-to-date) rather than the
        // result of the functional setState updater, which runs asynchronously.
        const { matched: patchedExisting } = patchConversationsWithMessage(
          conversationsRef.current || [],
          { conversationId, conversationType, message },
        );
        if (patchedExisting) {
          setConversations((prev) => patchConversationsWithMessage(prev, {
            conversationId,
            conversationType,
            message,
          }).conversations);
        }

        if (!patchedExisting && fetchConversationsRef.current) {
          if (sseConversationFetchTimeoutRef.current) {
            clearTimeout(sseConversationFetchTimeoutRef.current);
          }
          sseConversationFetchTimeoutRef.current = setTimeout(() => {
            sseConversationFetchTimeoutRef.current = null;
            fetchConversationsRef.current(false);
          }, 600);
        }

        if (onNewMessageCallbackRef.current) {
          const activeConv = activeConversationRef.current;
          if (activeConv) {
            const activeConvId = getConversationId(activeConv);
            const activeConvType = (activeConv.conversation_type || 'invite');

            if (
              String(activeConvId) === String(conversationId)
              && activeConvType === conversationType
            ) {
              onNewMessageCallbackRef.current({ conversationType, conversationId, message });
            }
          }
        }
      }
    });

    return () => {
      unsubscribe();
      window.removeEventListener('pointerdown', connectToEvents);
      window.removeEventListener('keydown', connectToEvents);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (sseConversationFetchTimeoutRef.current) {
        clearTimeout(sseConversationFetchTimeoutRef.current);
        sseConversationFetchTimeoutRef.current = null;
      }
      chatEventService.disconnect();
    };
  }, [isOpen, userType]);

  const setOnNewMessageCallback = useCallback((callback) => {
    onNewMessageCallbackRef.current = callback;
  }, []);

  const getFetchCount = useCallback(() => conversationFetchCountRef.current, []);

  // Stable action handlers — this object identity never changes after mount, so
  // action-only consumers never re-render due to chat state changes.
  const actionsValue = useMemo(() => ({
    open,
    close,
    toggle,
    openWithConversation,
    setActiveConversation,
    setUnreadCount,
    refresh: fetchConversations,
    getFetchCount,
    setOnNewMessageCallback,
  }), [
    open,
    close,
    toggle,
    openWithConversation,
    fetchConversations,
    getFetchCount,
    setOnNewMessageCallback,
  ]);

  const stateValue = useMemo(() => ({
    isOpen,
    conversations,
    loadingConversations,
    pendingOpenConversationId,
    activeConversation,
    userType,
    userId,
    unreadCount,
  }), [
    isOpen,
    conversations,
    loadingConversations,
    pendingOpenConversationId,
    activeConversation,
    userType,
    userId,
    unreadCount,
  ]);

  // Merged value preserves the original `useChat()` API for existing consumers.
  const value = useMemo(
    () => ({ ...stateValue, ...actionsValue }),
    [stateValue, actionsValue],
  );

  return (
    <ChatActionsContext.Provider value={actionsValue}>
      <ChatStateContext.Provider value={stateValue}>
        <ChatContext.Provider value={value}>
          {children}
        </ChatContext.Provider>
      </ChatStateContext.Provider>
    </ChatActionsContext.Provider>
  );
}
